import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import {
  createCompanySport,
  deleteCompanySport,
  getApiErrorMessage,
  listAllSports,
  updateCompanySport,
} from "../../services/adminCompanyService.js";

const CompanySportsManager = ({ idcompany, sports, onChanged }) => {
  const [catalogue, setCatalogue] = useState([]);
  const [selectedSportId, setSelectedSportId] = useState("");
  const [editingRelationId, setEditingRelationId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;

    listAllSports()
      .then((items) => {
        if (active) setCatalogue(items);
      })
      .catch((error) => {
        console.error("Error al cargar el catálogo de deportes", error);
        toast.error("No se pudo cargar el catálogo de deportes.");
      });

    return () => {
      active = false;
    };
  }, []);

  const editingSport = sports.find(
    (sport) => Number(sport.relationId) === Number(editingRelationId)
  );

  const selectableSports = useMemo(() => {
    const assignedIds = new Set(sports.map((sport) => Number(sport.idsport)));
    const currentSportId = Number(editingSport?.idsport);

    return catalogue
      .filter(
        (sport) =>
          !assignedIds.has(Number(sport.idsport)) ||
          Number(sport.idsport) === currentSportId
      )
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "es"));
  }, [catalogue, editingSport, sports]);

  const resetForm = () => {
    setSelectedSportId("");
    setEditingRelationId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedSportId) {
      toast.error("Seleccione un deporte.");
      return;
    }

    setLoading(true);
    try {
      if (editingRelationId) {
        await updateCompanySport({
          idcompanysport: editingRelationId,
          idcompany,
          idsport: Number(selectedSportId),
        });
        toast.success("Deporte actualizado para la compañía.");
      } else {
        await createCompanySport({
          idcompany,
          idsport: Number(selectedSportId),
        });
        toast.success("Deporte asignado a la compañía.");
      }

      resetForm();
      await onChanged?.();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "No se pudo guardar el deporte de la compañía.")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sport) => {
    setEditingRelationId(sport.relationId);
    setSelectedSportId(String(sport.idsport));
  };

  const handleDelete = async (sport) => {
    if (!window.confirm(`¿Quitar ${sport.name} de esta compañía?`)) return;

    setLoading(true);
    try {
      await deleteCompanySport(sport.relationId);
      toast.success("Deporte retirado de la compañía.");
      if (Number(editingRelationId) === Number(sport.relationId)) resetForm();
      await onChanged?.();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "No se pudo retirar el deporte de la compañía.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-600 dark:bg-gray-700">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
          Deportes habilitados
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-300">
          Estos son los deportes disponibles al crear eventos para la compañía.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mb-4 flex flex-col gap-2 sm:flex-row">
        <select
          value={selectedSportId}
          onChange={(event) => setSelectedSportId(event.target.value)}
          className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          disabled={loading}
        >
          <option value="">
            {editingRelationId ? "Seleccione el nuevo deporte" : "Seleccione un deporte"}
          </option>
          {selectableSports.map((sport) => (
            <option key={sport.idsport} value={sport.idsport}>
              {sport.name}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={loading || !selectedSportId}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {editingRelationId ? "Guardar cambio" : "Asignar deporte"}
        </button>
        {editingRelationId && (
          <button
            type="button"
            onClick={resetForm}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancelar
          </button>
        )}
      </form>

      {sports.length === 0 ? (
        <div className="rounded-lg bg-slate-50 px-4 py-5 text-center text-sm text-slate-500">
          La compañía todavía no tiene deportes asignados.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {sports.map((sport) => (
            <div
              key={sport.relationId || sport.idsport}
              className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 py-1.5 pl-3 pr-1.5 text-sm"
            >
              <span className="font-medium text-slate-700">{sport.name}</span>
              <button
                type="button"
                onClick={() => handleEdit(sport)}
                disabled={loading}
                className="rounded-full px-2 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-100"
              >
                Cambiar
              </button>
              <button
                type="button"
                onClick={() => handleDelete(sport)}
                disabled={loading}
                className="rounded-full px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-100"
              >
                Quitar
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default CompanySportsManager;

